/**
 * Research-report export helpers (§13.1, WP-R5). Two self-contained downloads for a
 * finalised report — NO external CDN/network at any point:
 *
 *  - Markdown: the raw report `.md` text, offered as a Blob download.
 *  - PDF: a rendered, print-styled document produced via the browser's own
 *    `window.print()` (a hidden same-origin iframe whose print dialog offers
 *    "Save as PDF"). This deliberately avoids bundling a heavy html→PDF library:
 *    the print path is dependency-free, fully offline, and renders the exact
 *    Markdown the report contains, with an inline print stylesheet. The user picks
 *    "Save as PDF" as the destination.
 *
 * These are pure browser-DOM utilities (no React); the run view wires them to
 * buttons. Kept out of the render tree so the print document is not styled by the
 * app's MUI theme (it carries its own minimal, self-contained CSS).
 */
import { parseMarkdownBlocks } from './markdownBlocks';

/** Trigger a client-side download of `text` as a file named `filename`. */
export function downloadTextFile(text: string, filename: string, mime = 'text/markdown'): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click has consumed the URL first.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Escape a string for safe interpolation into HTML text/attribute contexts. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Render the report Markdown subset (headings/paragraphs/lists/links/bold/code) to a
 * standalone HTML string, using the SAME block parser as the on-screen renderer
 * (`parseMarkdownBlocks`) so the print/PDF output can never diverge from what the
 * page shows. Self-contained — no external fonts/styles/scripts.
 */
function inlineToHtml(text: string): string {
  // Escape first, then re-introduce the inline spans. Escaped entities (`&amp;`,
  // `&quot;`) are valid inside text and `href="..."`, so this is safe.
  return escapeHtml(text)
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, t: string, u: string) => `<a href="${u}">${t}</a>`)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
}

export function markdownToHtml(md: string): string {
  return parseMarkdownBlocks(md)
    .map((block) => {
      if (block.type === 'heading') {
        return `<h${block.level}>${inlineToHtml(block.text)}</h${block.level}>`;
      }
      if (block.type === 'list') {
        const tag = block.ordered ? 'ol' : 'ul';
        return `<${tag}>${block.items.map((it) => `<li>${inlineToHtml(it)}</li>`).join('')}</${tag}>`;
      }
      return `<p>${inlineToHtml(block.text)}</p>`;
    })
    .join('\n');
}

/**
 * Print a fully-formed standalone HTML document via a hidden same-origin iframe, so the
 * user can pick "Save as PDF" as the destination. Self-contained, offline, no CDN — the
 * document must carry its own inline styles. Cleaned up after printing. Shared by the
 * report export (below) and the quick-chat PDF export (`lib/chatPdf.ts`) so both use one
 * print mechanism.
 */
export function printHtmlDocument(doc: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const cleanup = () => {
    // Defer removal so the print dialog has fully detached from the iframe.
    setTimeout(() => iframe.remove(), 500);
  };

  const win = iframe.contentWindow;
  const idoc = iframe.contentDocument ?? win?.document;
  if (!win || !idoc) {
    iframe.remove();
    return;
  }
  idoc.open();
  idoc.write(doc);
  idoc.close();

  const runPrint = () => {
    try {
      win.focus();
      win.print();
    } finally {
      cleanup();
    }
  };
  // Give the iframe a tick to lay out before printing.
  if (idoc.readyState === 'complete') {
    setTimeout(runPrint, 50);
  } else {
    iframe.onload = () => setTimeout(runPrint, 50);
  }
}

/**
 * Open the browser print dialog on a rendered version of the report, so the user can
 * "Save as PDF". Builds a self-contained, offline HTML document (no CDN) with an inline
 * print stylesheet and hands it to {@link printHtmlDocument}.
 */
export function printReportAsPdf(markdown: string, title: string): void {
  const bodyHtml = markdownToHtml(markdown);
  const doc = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  html, body { margin: 0; padding: 0; }
  body {
    font-family: "Inter", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #141414; line-height: 1.5; padding: 32px 40px;
  }
  h1 { font-size: 24px; font-weight: 700; margin: 0 0 12px; letter-spacing: -0.01em; }
  h2 { font-size: 19px; font-weight: 600; margin: 24px 0 8px; }
  h3 { font-size: 16px; font-weight: 600; margin: 18px 0 6px; }
  p { margin: 0 0 10px; }
  ul, ol { margin: 0 0 10px; padding-left: 24px; }
  li { margin: 2px 0; }
  a { color: #005ff3; }
  code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; background: #f5f5f5; padding: 1px 4px; border-radius: 3px; }
  @page { margin: 18mm; }
</style>
</head>
<body>${bodyHtml}</body>
</html>`;
  printHtmlDocument(doc);
}
